import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { 
  Box,
  Container,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Button,
  TextField,
  InputAdornment,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Divider,
  Snackbar,
  Alert,
  alpha,
  Stack
} from '@mui/material';

import { CalendarToday } from '@mui/icons-material';
import MyLocationIcon from '@mui/icons-material/MyLocation';
import SearchIcon from '@mui/icons-material/Search';
import PlaceIcon from '@mui/icons-material/Place';
import CloseIcon from '@mui/icons-material/Close';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';
import { Capacitor } from '@capacitor/core';
import BookMyOpAPI from '../services/BookMyOpapi';
import { searchAddressPredictions, getPlaceDetails, reverseGeocode } from '../services/googleMapsService';

// Import components
import Banner from './Home/Banner';
import HomeFavourites from './Home/HomeFavourites';
import SearchSection from './Home/SearchSection';
import Specialties from './Home/Specialties';
import HowItWorks from './Home/HowItWorks';
import Footer from './Home/Footer';
import TopDoctors from './Home/TopDoctors';
import DoctorsNearYou from './Home/DoctorsNearYou';
import TopHospitals from './Home/TopHospitals';
import HospitalsNearYou from './Home/HospitalsNearYou';
import ClinicList from './Home/ClinicList';
import SearchBar from './Home/SearchBar';
import DateRelative from './Home/DateRelative';
import SortingGrid from './Home/SortingGrid';
import DoctorsList from './Home/DoctorsList';
import HospitalList from './Home/HospitalList';
import SenioritySort from './Home/SenioritySort';
import BudgetSort from './Home/BudgetSort';
import LocationSelector from '../components/LocationSelector';

const BookmyOpHome = () => {  
  const navigate = useNavigate();
  const { isAuthenticated, user, userLocation, refreshProfile } = useBookMyOpAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isNativePlatform = Capacitor.isNativePlatform();
  
  // Modal and location state
  const [showLocationModal, setShowLocationModal] = useState(false);
  const [isDetectingLocation, setIsDetectingLocation] = useState(false);
  const [addressSearch, setAddressSearch] = useState('');
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [isSearchingAddress, setIsSearchingAddress] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const searchTimeoutRef = useRef(null);
  
  // Date filter state
  const [dateFilter, setDateFilter] = useState(null);
  
  // Sorting state - initialize to null so no card is highlighted by default
  const [sortOrder, setSortOrder] = useState(null);
  
  // Shared filter state for specialty and date selection
  const [selectedSpecialty, setSelectedSpecialty] = useState(null);
  const [selectedDateRange, setSelectedDateRange] = useState(null);

  // Check if user needs to add location
  useEffect(() => {
    if (isAuthenticated && (!userLocation || (!userLocation.latitude && !userLocation.longitude))) {
      setShowLocationModal(true);
    }
  }, [isAuthenticated, userLocation]);

  // Detect current location using GPS
  const handleDetectLocation = async () => {
    setIsDetectingLocation(true);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        async (position) => {
          try {
            const latitude = parseFloat(position.coords.latitude.toFixed(6));
            const longitude = parseFloat(position.coords.longitude.toFixed(6));
            
            // Reverse geocode to get address, city, state, and pincode
            let locationDetails = { latitude, longitude };
            try {
              const geocodedData = await reverseGeocode(latitude, longitude);
              locationDetails = {
                latitude: geocodedData.latitude,
                longitude: geocodedData.longitude,
                address: geocodedData.address,
                city: geocodedData.city,
                state: geocodedData.state,
                pincode: geocodedData.pincode
              };
              console.log('📍 Reverse geocoded location:', locationDetails);
            } catch (geocodeError) {
              console.warn('⚠️ Reverse geocoding failed, sending only coordinates:', geocodeError);
              // Continue with just lat/long if geocoding fails
            }
            
            // Use different API based on authentication status
            let response;
            if (isAuthenticated) {
              // Authenticated users: Save to profile (persistent)
              // Send same payload structure as address selection
              response = await BookMyOpAPI.updateLocation(locationDetails);
            } else {
              // Guest users: Save to session (temporary)
              // Send same payload structure as address selection
              response = await BookMyOpAPI.setSessionLocation(locationDetails);
              // Mark session as having location set
              sessionStorage.setItem('session_location_set', 'true');
            }

            if (response.status === 'success' || response.data) {
              const data = response.data || response;
              
              // Save to session storage for guest users
              if (!isAuthenticated) {
                sessionStorage.setItem('guest_location_data', JSON.stringify(data));
              }
              
              // Refresh profile for authenticated users
              if (isAuthenticated) {
                await refreshProfile(true);
              }
              
              setSnackbar({
                open: true,
                message: 'Location updated successfully!',
                severity: 'success'
              });

              setShowLocationModal(false);
              
              // Dispatch custom event to notify other components
              window.dispatchEvent(new CustomEvent('locationUpdated', { 
                detail: { locationData: data } 
              }));

              // For guest users, reload the page to refresh all location-based results
              if (!isAuthenticated) {
                setTimeout(() => {
                  window.location.reload();
                }, 500); // Small delay to show the success message
              }
            }
          } catch (error) {
            console.error('Failed to save location:', error, error?.response, error?.message);
            setSnackbar({
              open: true,
              message: 'Failed to update location: ' +
                (error?.response?.data?.message || error?.response?.data?.detail || error?.message || JSON.stringify(error?.response?.data) || 'Unknown error'),
              severity: 'error'
            });
          } finally {
            setIsDetectingLocation(false);
          }
        },
        (error) => {
          console.error('Geolocation error:', error);
          setIsDetectingLocation(false);
          setSnackbar({
            open: true,
            message: 'Please enable location access in your browser',
            severity: 'error'
          });
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setIsDetectingLocation(false);
      setSnackbar({
        open: true,
        message: 'Geolocation is not supported by your browser',
        severity: 'error'
      });
    }
  };

  // Address search handler
  const handleAddressSearchChange = (event) => {
    const query = event.target.value;
    setAddressSearch(query);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (query.trim().length < 3) {
      setAddressSuggestions([]);
      return;
    }

    searchTimeoutRef.current = setTimeout(() => {
      searchAddress(query);
    }, 500);
  };

  const searchAddress = async (query) => {
    setIsSearchingAddress(true);
    try {
      const predictions = await searchAddressPredictions(query);
      setAddressSuggestions(predictions);
    } catch (error) {
      console.error('Address search error:', error);
      if (error.message.includes('API key not configured')) {
        setSnackbar({
          open: true,
          message: 'Google Maps API key not configured',
          severity: 'error'
        });
      }
      setAddressSuggestions([]);
    } finally {
      setIsSearchingAddress(false);
    }
  };

  const handleAddressSelect = async (suggestion) => {
    setIsDetectingLocation(true);
    setAddressSearch('');
    setAddressSuggestions([]);

    try {
      const placeDetails = await getPlaceDetails(suggestion.place_id);
      
      // Use different API based on authentication status
      let response;
      if (isAuthenticated) {
        // Authenticated users: Save to profile (persistent)
        // Send parsed city, state, pincode from Google Places to backend
        response = await BookMyOpAPI.updateLocation({
          latitude: placeDetails.latitude,
          longitude: placeDetails.longitude,
          address: placeDetails.address,
          city: placeDetails.city,
          state: placeDetails.state,
          pincode: placeDetails.pincode
        });
      } else {
        // Guest users: Save to session (temporary)
        response = await BookMyOpAPI.setSessionLocation({
          latitude: placeDetails.latitude,
          longitude: placeDetails.longitude,
          address: placeDetails.address,
          city: placeDetails.city,
          state: placeDetails.state,
          pincode: placeDetails.pincode
        });
        // Mark session as having location set
        sessionStorage.setItem('session_location_set', 'true');
      }

      if (response.status === 'success' || response.data) {
        const data = response.data || response;
        
        // Save to session storage for guest users
        if (!isAuthenticated) {
          sessionStorage.setItem('guest_location_data', JSON.stringify(data));
        }
        
        // Refresh profile for authenticated users
        if (isAuthenticated) {
          await refreshProfile(true);
        }
        
        setSnackbar({
          open: true,
          message: 'Location updated successfully!',
          severity: 'success'
        });

        setShowLocationModal(false);

        // Dispatch custom event to notify other components
        window.dispatchEvent(new CustomEvent('locationUpdated', { 
          detail: { locationData: data } 
        }));

        // For guest users, reload the page to refresh all location-based results
        if (!isAuthenticated) {
          setTimeout(() => {
            window.location.reload();
          }, 500); // Small delay to show the success message
        }
      }
    } catch (error) {
      console.error('Failed to save location:', error, error?.response, error?.message);
      setSnackbar({
        open: true,
        message: 'Failed to update location: ' +
          (error?.response?.data?.message || error?.response?.data?.detail || error?.message || JSON.stringify(error?.response?.data) || 'Unknown error'),
        severity: 'error'
      });
    } finally {
      setIsDetectingLocation(false);
    }
  };

  const handleClearSearch = () => {
    setAddressSearch('');
    setAddressSuggestions([]);
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseModal = () => {
    setShowLocationModal(false);
  };

  // Handle location update - refresh page to show updated results
  const handleLocationUpdate = (locationData) => {
    setSnackbar({
      open: true,
      message: 'Location updated successfully! Results will refresh.',
      severity: 'success'
    });
    
    // Reload page after a short delay to refresh all location-based results
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  };

  // Handle date range selection from DateRelative component
  const handleDateRangeSelect = (dateRange) => {
    setDateFilter(dateRange);
    setSelectedDateRange(dateRange);
    // Only navigate if specialty is already selected
    if (selectedSpecialty) {
      navigateToFindDoctors(selectedSpecialty, dateRange);
    }
  };

  // Handle specialty selection from Specialties component
  const handleSpecialtySelect = (specialtyName) => {
    setSelectedSpecialty(specialtyName);
    // Navigate immediately when specialty is selected (date is optional)
    navigateToFindDoctors(specialtyName, selectedDateRange);
  };

  // Navigate to find-doctors with both filters
  const navigateToFindDoctors = (specialty, dateRange) => {
    const params = new URLSearchParams();
    
    if (specialty) {
      params.set('specialty', encodeURIComponent(specialty));
    }
    
    if (dateRange) {
      if (dateRange.availableDate) {
        params.set('availableDate', dateRange.availableDate);
      } else {
        if (dateRange.availableDateStart) {
          params.set('availableDateStart', dateRange.availableDateStart);
        }
        if (dateRange.availableDateEnd) {
          params.set('availableDateEnd', dateRange.availableDateEnd);
        }
      }
    }
    
    // Always include sort parameter for consistency
    if (sortOrder) {
      params.set('sort', sortOrder);
    }
    
    const queryString = params.toString();
    navigate(`/find-doctors${queryString ? `?${queryString}` : ''}`);
  };

  // Handle sort selection with navigation
  const handleSortSelectWithNavigation = (sortValue, type) => {
    // Update sort order state
    setSortOrder(sortValue);
    
    const params = new URLSearchParams();
    params.set('sort', sortValue);
    
    // Add date filter if exists
    if (selectedDateRange) {
      if (selectedDateRange.availableDate) {
        params.set('availableDate', selectedDateRange.availableDate);
      } else {
        if (selectedDateRange.availableDateStart) {
          params.set('availableDateStart', selectedDateRange.availableDateStart);
        }
        if (selectedDateRange.availableDateEnd) {
          params.set('availableDateEnd', selectedDateRange.availableDateEnd);
        }
      }
    }
    
    // Navigate based on type
    if (type === 'hospitals') {
      // On mobile, use route path; on desktop, use query param
      if (isMobile) {
        navigate(`/find-doctors/hospitals?${params.toString()}`);
      } else {
        params.set('list', 'hospitals');
        navigate(`/find-doctors?${params.toString()}`);
      }
    } else {
      // Doctors navigation
      navigate(`/find-doctors?${params.toString()}`);
    }
  };

  return (
    <>
      <Box sx={{ minHeight: '100vh', minHeight: '100dvh' ,bgcolor:theme.palette.background.default}}>
        <Container maxWidth="xxl" sx={{ padding: '0 !important' }}>
        
          <Banner />
          
          {/* Location Selector - Search Bar Style */}
          <Container maxWidth="xl">
          <Stack
          direction="row"
          alignItems="center"
          spacing={2}
          sx={{ mb: 2 }}
        >
          <CalendarToday
            sx={{
              fontSize: { xs: 24, md: 28 },
              color: theme.palette.primary.main,
            }}
          />
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.1rem', md: '1.3rem' },
              color: theme.palette.text.primary,
            }}
          >
            Find Doctors Available
          </Typography>
        </Stack>
        </Container>
          <SearchBar/>
          <Container maxWidth="xl">
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                gap: 1,
                py: 1,
                // Allow content to shrink on small screens instead of wrapping
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  flex: '1 1 auto',
                  minWidth: 0,
                }}
              >
                <LocationSelector
                  onLocationUpdate={handleLocationUpdate}
                  placeholder="Search in different location"
                  fullWidth
                  showCurrentLocation={false}
                  sx={{ maxWidth: '100%' }}
                />
              </Box>

              <Box
                sx={{
                  flex: '0 0 auto',
                  minWidth: { xs: 120, sm: 160, md: 200 },
                  maxWidth: { xs: 160, sm: 200, md: 260 },
                }}
              >
                <DateRelative
                  onDateRangeSelect={handleDateRangeSelect}
                  navigateOnClick={false}
                />
              </Box>
            </Box>
          </Container>
          <Specialties onSpecialtySelect={handleSpecialtySelect} />
          {/* <SortingGrid 
            onSortSelectWithNavigation={handleSortSelectWithNavigation}
            currentSort={sortOrder}
            userLocation={userLocation}
            selectedDateRange={selectedDateRange}
          /> */}
          <HomeFavourites />
          <DoctorsList dateFilter={dateFilter} sortOrder={sortOrder} />
          <HospitalList dateFilter={dateFilter} sortOrder={sortOrder} />
         
          <TopDoctors />
          <DoctorsNearYou />
          <SenioritySort />
          <BudgetSort />
          <TopHospitals />
          <HospitalsNearYou />
          {/* <ClinicList /> */}
          <Box sx={{ display: { xs: 'none', md: 'block' } }}>
            <HowItWorks />
          </Box>
          <Footer />
        </Container>
      </Box>

      {/* Location Setup Modal */}
      <Dialog 
        open={showLocationModal} 
        onClose={handleCloseModal}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '24px',
            p: 2,
            backdropFilter: 'blur(10px)',
          }
        }}
      >
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: 2,
          pb: 1
        }}>
          <LocationOnOutlinedIcon 
            sx={{ 
              fontSize: '2rem', 
              color: theme.palette.primary.main 
            }} 
          />
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" fontWeight={700}>
              Add Your Location
            </Typography>
            <Typography variant="body1" color="text.secondary" fontWeight={600}>
              Help us show you nearby doctors and hospitals
            </Typography>
          </Box>
          <IconButton 
            onClick={handleCloseModal}
            sx={{ 
              color: 'text.secondary',
              '&:hover': { 
                background: alpha(theme.palette.error.main, 0.1),
                color: theme.palette.error.main 
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent>
          {/* Detect Location Button */}
          <Button
            fullWidth
            variant="contained"
            size="large"
            onClick={handleDetectLocation}
            disabled={isDetectingLocation}
            startIcon={
              isDetectingLocation ? (
                <CircularProgress size={20} sx={{ color: 'white' }} />
              ) : (
                <MyLocationIcon />
              )
            }
            sx={{
              py: 2,
              borderRadius: '16px',
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
              fontWeight: 600,
              fontSize: '1rem',
              textTransform: 'none',
              mb: 3,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              '&:hover': {
                boxShadow: '0 12px 32px rgba(0,0,0,0.18)',
                transform: 'translateY(-2px)',
              },
              '&:disabled': {
                opacity: 0.7,
              }
            }}
          >
            {isDetectingLocation ? 'Detecting Location...' : 'Detect My Location'}
          </Button>

          <Divider sx={{ mb: 3 }}>
            <Typography variant="body1" color="text.secondary" fontWeight={900}>
              OR SEARCH ADDRESS
            </Typography>
          </Divider>

          {/* Address Search */}
          <TextField
            fullWidth
            placeholder="Search city, area, or address..."
            value={addressSearch}
            onChange={handleAddressSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.primary' }} />
                </InputAdornment>
              ),
              endAdornment: addressSearch && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={handleClearSearch}>
                    <CloseIcon sx={{ fontSize: '1rem' }} />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '16px',
                backgroundColor: theme.palette.background.paper,
                '&:hover fieldset': {
                  borderColor: theme.palette.primary.main,
                },
              }
            }}
          />

          {/* Address Suggestions */}
          {isSearchingAddress && (
            <Box sx={{ py: 4, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          )}

          {!isSearchingAddress && addressSuggestions.length > 0 && (
            <List sx={{ mt: 2, maxHeight: 300, overflowY: 'auto' }}>
              {addressSuggestions.map((suggestion, index) => (
                <ListItem key={suggestion.place_id || index} disablePadding>
                  <ListItemButton
                    onClick={() => handleAddressSelect(suggestion)}
                    sx={{
                      borderRadius: '12px',
                      mb: 1,
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      '&:hover': {
                        background: alpha(theme.palette.primary.main, 0.08),
                        borderColor: theme.palette.primary.main,
                      }
                    }}
                  >
                    <ListItemIcon sx={{ minWidth: 40 }}>
                      <PlaceIcon sx={{ color: theme.palette.primary.main }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={600}>
                          {suggestion.structured_formatting?.main_text || suggestion.description}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="body2" color="text.primary">
                          {suggestion.structured_formatting?.secondary_text || suggestion.description}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}

          {!isSearchingAddress && addressSearch.length >= 3 && addressSuggestions.length === 0 && (
            <Box sx={{ py: 3, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No locations found. Try a different search.
              </Typography>
            </Box>
          )}

          {/* Skip Button */}
          {/* <Box sx={{ mt: 3, textAlign: 'center' }}>
            <Button
              onClick={handleCloseModal}
              sx={{
                color: 'text.secondary',
                textTransform: 'none',
                '&:hover': {
                  background: alpha(theme.palette.primary.main, 0.05),
                }
              }}
            >
              Skip for now
            </Button>
          </Box> */}
        </DialogContent>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          variant="filled"
          sx={{ 
            borderRadius: '12px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default BookmyOpHome;
